import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../../shared/api/errors';
import { updateSource, type TSource } from '../../entities/source';

export interface IGeneralAddress {
  open: boolean;
  pending: boolean;
  set: (next: boolean) => void;
  close: () => void;
  refusal: string | null;
}

const NO_OTHER_ADDRESS =
  'This source has no client address, so closing the general one would leave nowhere for a ' +
  'request to arrive. Disable the whole source instead.';

/** `message` falls back to the bare code when the server sent none, so anything without a space is replaced. */
const refusalOf = (error: unknown): string | null => {
  if (error === null || error === undefined) return null;
  if (!(error instanceof ApiError)) return 'The address could not be changed. Nothing was saved.';
  if (/\s/.test(error.message)) return error.message;
  return error.code === 'sources.general_address_close_leaves_no_address'
    ? NO_OTHER_ADDRESS
    : 'The address could not be changed. Nothing was saved.';
};

export const useGeneralAddress = (source: TSource): IGeneralAddress => {
  const client = useQueryClient();

  const toggle = useMutation({
    mutationFn: (next: 'open' | 'closed') => updateSource(source.id, { generalAddressState: next }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['source', source.id] });
      void client.invalidateQueries({ queryKey: ['sources'] });
    },
  });

  const set = (next: boolean) => toggle.mutate(next ? 'open' : 'closed');

  return {
    open: source.generalAddressState === 'open',
    pending: toggle.isPending,
    set,
    close: () => set(false),
    refusal: refusalOf(toggle.error),
  };
};
